/**
 * Comprehensive Image Caching System
 * Prevents image reloading when navigating between pages
 */

class ImageCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.preloadQueue = new Set();
    this.maxCacheSize = 100; // Maximum number of images to cache
    this.cacheExpiry = 30 * 60 * 1000; // 30 minutes in milliseconds
    
    // Initialize browser cache API if available
    this.initServiceWorkerCache();
  }

  /**
   * Initialize Service Worker cache for persistent storage
   */
  async initServiceWorkerCache() {
    if ('caches' in window) {
      try {
        this.browserCache = await caches.open('gifted-solutions-images-v1');
      } catch (error) {
        console.warn('Could not initialize browser cache:', error);
      }
    }
  }

  /**
   * Generate cache key for an image
   */
  getCacheKey(url, options = {}) {
    const { width, height, quality } = options;
    let key = url;
    if (width || height || quality) {
      key += `_${width || 'auto'}x${height || 'auto'}_q${quality || 80}`;
    }
    return key;
  }

  /**
   * Check if image is cached and not expired
   */
  isCached(url, options = {}) {
    const key = this.getCacheKey(url, options);
    const cached = this.cache.get(key);
    
    if (!cached) return false;
    
    // Check if cache has expired
    if (Date.now() - cached.timestamp > this.cacheExpiry) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }

  /**
   * Get cached image
   */
  getCached(url, options = {}) {
    const key = this.getCacheKey(url, options);
    const cached = this.cache.get(key);
    
    if (cached && Date.now() - cached.timestamp <= this.cacheExpiry) {
      // Update access time for LRU
      cached.lastAccessed = Date.now();
      return cached.url;
    }
    
    return null;
  }

  /**
   * Cache an image
   */
  setCached(url, cachedUrl, options = {}) {
    const key = this.getCacheKey(url, options);
    
    // Implement LRU cache eviction
    if (this.cache.size >= this.maxCacheSize) {
      this.evictOldest();
    }
    
    this.cache.set(key, {
      url: cachedUrl,
      originalUrl: url,
      timestamp: Date.now(),
      lastAccessed: Date.now(),
      options
    });

    // Also cache in browser cache if available
    this.cacheInBrowser(url, cachedUrl);
  }

  /**
   * Evict oldest cached images
   */
  evictOldest() {
    let oldestKey = null;
    let oldestTime = Date.now();
    
    for (const [key, value] of this.cache.entries()) {
      if (value.lastAccessed < oldestTime) {
        oldestTime = value.lastAccessed;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Cache image in browser cache
   */
  async cacheInBrowser(originalUrl, cachedUrl) {
    if (!this.browserCache) return;
    
    try {
      const response = await fetch(cachedUrl);
      if (response.ok) {
        await this.browserCache.put(originalUrl, response.clone());
      }
    } catch (error) {
      console.warn('Could not cache in browser:', error);
    }
  }

  /**
   * Load and cache an image
   */
  async loadImage(url, options = {}) {
    if (!url) return null;

    const key = this.getCacheKey(url, options);
    
    // Return cached version if available
    const cached = this.getCached(url, options);
    if (cached) {
      return cached;
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(key)) {
      return this.loadingPromises.get(key);
    }

    // Create new loading promise
    const loadingPromise = this.createLoadingPromise(url, options);
    this.loadingPromises.set(key, loadingPromise);

    try {
      const result = await loadingPromise;
      this.setCached(url, result, options);
      return result;
    } finally {
      this.loadingPromises.delete(key);
    }
  }

  /**
   * Create a promise to load an image
   */
  createLoadingPromise(url, options = {}) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      // Set crossOrigin for CORS images
      if (url.includes('http') && !url.includes(window.location.origin)) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        // Create object URL for caching
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const objectUrl = URL.createObjectURL(blob);
            resolve(objectUrl);
          } else {
            resolve(url); // Fallback to original URL
          }
        }, 'image/jpeg', 0.9);
      };

      img.onerror = () => {
        console.warn('Failed to load image:', url);
        resolve(url); // Return original URL as fallback
      };

      img.src = url;
    });
  }

  /**
   * Preload images for better performance
   */
  async preloadImages(urls, options = {}) {
    const preloadPromises = urls.map(url => {
      if (!this.isCached(url, options)) {
        return this.loadImage(url, options);
      }
      return Promise.resolve(this.getCached(url, options));
    });

    try {
      await Promise.allSettled(preloadPromises);
    } catch (error) {
      console.warn('Error preloading images:', error);
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    // Revoke object URLs to prevent memory leaks
    for (const [key, cached] of this.cache.entries()) {
      if (cached.url.startsWith('blob:')) {
        URL.revokeObjectURL(cached.url);
      }
    }
    
    this.cache.clear();
    this.loadingPromises.clear();
    
    // Clear browser cache
    if (this.browserCache) {
      this.browserCache.keys().then(keys => {
        keys.forEach(key => this.browserCache.delete(key));
      });
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      maxCacheSize: this.maxCacheSize,
      loadingPromises: this.loadingPromises.size,
      cacheHitRate: this.calculateHitRate()
    };
  }

  /**
   * Calculate cache hit rate
   */
  calculateHitRate() {
    // This would need to be implemented with request tracking
    return 0; // Placeholder
  }
}

// Create singleton instance
const imageCache = new ImageCache();

export default imageCache;

// Export utility functions
export const loadCachedImage = (url, options) => imageCache.loadImage(url, options);
export const preloadImages = (urls, options) => imageCache.preloadImages(urls, options);
export const clearImageCache = () => imageCache.clearCache();
export const getImageCacheStats = () => imageCache.getStats();
